import { ApolloClient, useApolloClient } from '@apollo/client';
import { BuilderTemplateApi, justParams } from '@deltasierra/shared';
import { failureResult, isNullOrUndefined, Result, successResult } from '@deltasierra/type-utilities';
import * as Sentry from '@sentry/browser';
import * as React from 'react';
import { useAngularServiceContext } from '../../../../../common/componentUtils/angularServiceContexts';
import { invokeApiRoute } from '../../../../../common/httpUtils';
import { GetBuildableTemplate, GetBuildableTemplateVariables } from './__graphqlTypes/GetBuildableTemplate';
import { GET_BUILDABLE_TEMPLATE } from './GetBuildableTemplate.query';
import { ParsedTemplateId, ParsedValidTemplateId } from './use-template-id-state';
import { BuildableTemplate, BuilderTemplate, ValidatedTemplate } from './validated-template';

async function fetchBuilderTemplate(
    httpService: ng.IHttpService,
    templateId: number,
): Promise<Result<BuilderTemplate>> {
    try {
        const response = await invokeApiRoute(
            httpService,
            BuilderTemplateApi.getBuilderTemplate,
            justParams({ templateId }),
        );
        if (!response) {
            return failureResult(new Error('Invalid response from getBuilderTemplate API'));
        }
        const { compositeImage, graphqlId, id } = response;
        return successResult({
            graphqlId,
            id,
            thumbnail: compositeImage?.thumb256x256url ?? undefined,
            type: 'BuilderTemplate',
        });
    } catch (error) {
        Sentry.captureException(error);
        return failureResult(error);
    }
}

async function fetchBuildableTemplate(
    buildableTemplateId: string,
    apolloClient: ApolloClient<unknown>,
): Promise<Result<BuildableTemplate>> {
    try {
        const response = await apolloClient.query<GetBuildableTemplate, GetBuildableTemplateVariables>({
            errorPolicy: 'ignore',
            query: GET_BUILDABLE_TEMPLATE,
            variables: {
                id: buildableTemplateId,
            },
        });
        if (!response) {
            return failureResult(new Error('Invalid response from getBuildableTemplate query'));
        }
        const {
            data: { buildableTemplate },
        } = response;

        if (isNullOrUndefined(buildableTemplate)) {
            return failureResult(new Error('Buildable template not found'));
        }

        return successResult({
            graphqlId: buildableTemplate.id,
            id: buildableTemplate.id,
            templateType: buildableTemplate.templateType,
            thumbnail: buildableTemplate.currentVersion.thumbnailUrl ?? undefined,
            type: 'BuildableTemplate',
        });
    } catch (error) {
        Sentry.captureException(error);
        return failureResult(error);
    }
}

type TemplateWithState = {
    loading: boolean;
    template: ValidatedTemplate | null;
};

type UseValidatedTemplateReturns = TemplateWithState;

export function useValidatedTemplate(parsedId: ParsedTemplateId): UseValidatedTemplateReturns {
    const apolloClient = useApolloClient();
    const httpService = useAngularServiceContext('$http');

    const [state, setState] = React.useState<TemplateWithState>({
        loading: true,
        template: null,
    });

    const validate = React.useCallback(
        async (parsedValidId: ParsedValidTemplateId) => {
            let result: Result<ValidatedTemplate>;
            if (parsedValidId.type === 'builder-template') {
                result = await fetchBuilderTemplate(httpService, parsedValidId.id);
            } else {
                result = await fetchBuildableTemplate(parsedValidId.id, apolloClient);
            }

            return result;
        },
        [apolloClient, httpService],
    );

    React.useEffect(() => {
        if (parsedId.type === 'empty' || parsedId.type === 'invalid') {
            setState({ loading: false, template: null });
            return;
        }

        // This is necessary to prevent setting state on an unmounted component if the user changes the input rapidly
        let cancelled = false;

        setState({ loading: true, template: null });

        void validate(parsedId).then(result => {
            if (cancelled) {
                return;
            }
            if (result.success) {
                setState({ loading: false, template: result.value });
            } else {
                setState({ loading: false, template: null });
            }
        });

        // eslint-disable-next-line consistent-return
        return () => {
            cancelled = true;
        };
    }, [parsedId, validate]);

    return state;
}
